import { FormAction } from '@anytag/shared-types';
import { objectKeys } from '@anytag/shared-assets';
import { useQueryHelper } from '@anytag/shared-hooks';
import {
  namedOperations,
  CmsChannelAssetInput,
  useLinkCmsChannelAssetsMutation,
  useYoutubeCmsChannelInfoPromiseQuery
} from '@anytag/graphql';
import { YoutubeCMSAssetOwnerChannelIdFormValues } from '../YoutubeCMSAssetsList';

export const useUpdateYoutubeAssetChannelId = () => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { getYoutubeCmsChannelInfo } = useYoutubeCmsChannelInfoPromiseQuery();
  const { callLinkCmsChannelAssets } = useLinkCmsChannelAssetsMutation({
    refetchQueries: [namedOperations.Query.AllYoutubeCmsEstimateAssets]
  });

  const handleSaveChange: FormAction<YoutubeCMSAssetOwnerChannelIdFormValues>['onSubmit'] = async (
    values,
    { formState, setError, reset }
  ) => {
    const dirtyFieldKeys = objectKeys(formState.dirtyFields);
    const channelIds = dirtyFieldKeys.reduce<string[]>(
      (acc, curr) => (!!values[curr] && !acc.includes(values[curr]) ? [...acc, values[curr]] : acc),
      []
    );

    try {
      const { data } = await getYoutubeCmsChannelInfo({ variables: { channelIds } });
      const channelInfo = data.youtubeCmsChannelInfo;

      if (channelInfo?.length !== channelIds.length) {
        const channelInfoIds = channelInfo?.map((channel) => channel.channelId) || [];

        dirtyFieldKeys.forEach((key) => {
          if (!channelInfoIds.includes(values[key]!)) {
            setError(key, { message: t<string>('Cannot find the channel') }, { shouldFocus: true });
          }
        });

        throw new Error(t<string>('Cannot find the channel'));
      }

      await callLinkCmsChannelAssets({
        variables: {
          input: {
            records: dirtyFieldKeys.reduce<CmsChannelAssetInput[]>((acc, curr) => {
              const channel = channelInfo.find((c) => c.channelId === values[curr]);

              return channel
                ? [
                    ...acc,
                    {
                      id: Number(curr.split('-').at(0)!),
                      channelId: channel.channelId,
                      channelAvatar: channel.avatar,
                      channelName: channel.channelName
                    }
                  ]
                : acc;
            }, [])
          }
        }
      });

      enqueueSnackbar(t('succeededInSave'), { variant: 'success' });
      reset(values);
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  };

  return { handleSaveChange };
};
